import * as React from 'react'
import { TrendUp } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { Card, CardHeader, CardTitle, CardAction, CardContent } from '../core/card'
import { type PeriodValue } from '../core/period-picker'
import {
  LineChart,
  PerformanceControls,
  type LineChartData,
} from './performance-charts'

/**
 * PipelineTrendWidget — pipeline value + volume over time as a dual-axis line
 * chart: value series (Unweighted / Weighted) on the left `$` axis and a Deal
 * Count series on the right axis (mark that series `axis: 'right'`). Wraps the
 * shared {@link LineChart}. Distinct from `PipelineWidget` (current stage
 * breakdown) — this is pipeline *over time*.
 *
 *   <PipelineTrendWidget data={{ labels, series, leftFormat, rightFormat }} />
 */

export interface PipelineTrendWidgetProps {
  data: LineChartData
  periodLabel?: React.ReactNode
  defaultPeriod?: PeriodValue
  onPeriodApply?: (value: PeriodValue) => void
  /** Wire the ⋯ menu's "Hide widget" action; hidden when omitted. */
  onHide?: () => void
  title?: React.ReactNode
  className?: string
}

export const PipelineTrendWidget = React.forwardRef<
  HTMLDivElement,
  PipelineTrendWidgetProps
>(
  (
    { data, periodLabel = 'Period', defaultPeriod, onPeriodApply, onHide, title = 'Pipeline Trend', className },
    ref
  ) => (
    <Card ref={ref} className={cn('flex w-full max-w-2xl flex-col', className)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendUp aria-hidden="true" className="size-4 text-fbui-gray-500" />
          {title}
        </CardTitle>
        <CardAction>
          <PerformanceControls
            periodLabel={periodLabel}
            defaultPeriod={defaultPeriod}
            onPeriodApply={onPeriodApply}
            onHide={onHide}
            hideLabel="Pipeline trend options"
          />
        </CardAction>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col">
        <LineChart data={data} />
      </CardContent>
    </Card>
  )
)
PipelineTrendWidget.displayName = 'PipelineTrendWidget'
